/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Article } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import { Box, Button, TableContainer } from "@mui/material";
import axios from "axios";
import { signOut } from "firebase/auth";
import { marked } from "marked";
import { useState } from "react";

import { auth } from "../../auth/firebase-config";
import FeedbackPopup from "../copilot/feedback-popup";
import { UseSnackbar } from "../snackbar/snackbarContext";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import PDFViewer from "./pdf-viewer-iframe";

const messageRow = css`
  display: flex;
`;

const messageRowRight = css`
  display: flex;
  justify-content: flex-end;
`;

const messageBlue = css`
  position: relative;
  margin-left: 20px;
  margin-bottom: 10px;

  background-color: #eaeaea;
  min-width: 85%;
  text-align: left;

  border: 1px solid #eaeaea;
  border-radius: 10px;
  &:after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border-top: 15px solid #d9d9d;
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    top: 0;
    left: -15px;
  }
  &:before {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border-top: 17px solid #eaeaea;
    border-left: 16px solid transparent;
    border-right: 16px solid transparent;
    top: -1px;
    left: -17px;
  }
`;

const messageOrange = css`
  position: relative;
  margin-right: 20px;
  margin-bottom: 10px;

  background-color: #eaeaea;

  text-align: left;

  border: 1px solid #eaeaea;
  border-radius: 10px;
  &:after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border-top: 15px solid #eaeaea;
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    top: 0;
    right: -15px;
  }
  &:before {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border-top: 17px solid #eaeaea;
    border-left: 16px solid transparent;
    border-right: 16px solid transparent;
    top: -1px;
    right: -17px;
  }
`;

const messageContent = css`
  padding: 1rem;
  margin: 0;
`;

const displayName1 = css`
  font-size: normal;
  margin-left: 20px;
`;

export const MessageLeft = ({
  covloader,
  loading,
  loadversion,
  setloadversion,
  setGcs_generated_documents,
  setVersion,
  message,
  handlesetchat,
  sdata,
  handlealldocs,
  user_edit,
  citations,
  isApiError,
  isTimeoutError,
  responseTime,
  messageId,
}: any) => {
  const showSnackbar = UseSnackbar();
  const handleSaveVersion = async (chat_data) => {
    const API_URL = `${import.meta.env.VITE_API_URL}/upload-attachment`;
    const extractFileName = (path) => path.split("/").pop().replace(/_/g, " ");

    var gcs_url = "";
    const accessToken = localStorage.getItem("accessToken");
    const file = new File([chat_data], "save.txt", { type: "text/plain" });
    const formData = new FormData();
    formData.append(`files`, file);
    try {
      const response = await axios.post(API_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response) {
        gcs_url = response.data.data[0];
      } else {
        throw new Error("Invalid response format");
      }
      return response;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        signOut(auth)
          .then(() => {
            localStorage.removeItem("userInfo");
            localStorage.removeItem("accessToken");
            localStorage.removeItem("user_data");
            console.log("User signed out due to inactivity.");
          })
          .catch((signOutError) => {
            console.error("Error signing out:", signOutError);
          });
      } else {
        showSnackbar("Something Went Wrong! Please Try again! ", "error");
        throw new Error(`Internal server error: ${error}`);
      }
    }
  };

  const getall_docs = async (conversationId) => {
    const accessToken = localStorage.getItem("accessToken");
    const skip = 0;
    const limit = 0;

    const API_URL = `${
      import.meta.env.VITE_API_URL
    }/documents_on_conversation/${conversationId}?skip=${skip}&limit=${limit}`;

    try {
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      return response;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        signOut(auth)
          .then(() => {
            localStorage.removeItem("userInfo");
            localStorage.removeItem("accessToken");
            localStorage.removeItem("user_data");
            console.log("User signed out due to inactivity.");
          })
          .catch((signOutError) => {
            console.error("Error signing out:", signOutError);
          });
      } else {
        showSnackbar("Something Went Wrong!", "error");

        throw new Error(`Internal server error: ${error}`);
      }
      throw new Error(`Internal server error: ${error}`);
    }
  };

  const handleSave = async (message, sdata, handlealldocs) => {
    setloadversion(true);
    try {
      const response = await handleSaveVersion(message);

      const res = await getall_docs(sdata[0]);

      const resdata = res.data.data.Documents;
      const extractedData = resdata.map((doc) => ({
        gcs_url: doc.doc_url,
        document_version: doc.document_version,
      }));
      const sortedData = extractedData.sort(
        (a, b) => b.document_version - a.document_version
      );

      if (sortedData.length > 0) {
        const highestVersion = sortedData[0].document_version;

        let incrementedVersion;
        if (highestVersion >= 0 && highestVersion < 1) {
          incrementedVersion = 1;
        } else if (highestVersion >= 1 && highestVersion < 2) {
          incrementedVersion = 2;
        } else {
          incrementedVersion = Math.floor(highestVersion) + 1;
        }
        const vers = incrementedVersion.toFixed(0);

        incrementedVersion = incrementedVersion.toFixed(1);
        setVersion(vers);
        if (
          response &&
          response.data &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          const gcs_url = response.data.data[0];
          setGcs_generated_documents(gcs_url);
          await savedocfirebase(gcs_url, incrementedVersion, sdata);
          const res = await getall_docs(sdata[0]);

          handlealldocs(res.data.data.Documents);
        } else {
          throw new Error("Invalid response format from handleSaveVersion");
        }
      }
    } catch (error) {
      showSnackbar("Something Went Wrong! Please Try Again! ", "error");
    } finally {
      setloadversion(false);
    }
  };

  const savedocfirebase = async (gcs_url, incrementedVersion, sdata) => {
    const API_URL = `${import.meta.env.VITE_API_URL}/Document`;
    const accessToken = localStorage.getItem("accessToken");
    const user_data = localStorage.getItem("user_data");
    const documentData = {
      conversations_id: sdata[0],
      doc_url: gcs_url,
      document_name: "",
      document_version: incrementedVersion,
      project_name: sdata[1].project_name,
      type: sdata[2],
      user_id: JSON.parse(user_data).id,
      feedback: 1,
      response_time: responseTime,
    };

    try {
      const response = await axios.post(API_URL, documentData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response && response.status === 200) {
        showSnackbar("Document new version created", "success");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error saving document:", error);
      if (error.response && error.response.status === 401) {
        signOut(auth)
          .then(() => {
            localStorage.removeItem("userInfo");
            localStorage.removeItem("accessToken");
            localStorage.removeItem("user_data");
            console.log("User signed out due to inactivity.");
          })
          .catch((signOutError) => {
            console.error("Error signing out:", signOutError);
          });
      } else {
        showSnackbar("Something Went Wrong! Please Try Again! ", "error");
        throw new Error(`Internal server error: ${error}`);
      }
    }
  };

  const handle_messageversion = () => {
    handlesetchat(JSON.stringify(message));
    handleSave(JSON.stringify(message), sdata, handlealldocs);
  };
  const content = marked(message);

  const formatResponseTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(0);
    return `${minutes} min ${seconds < 10 ? "0" : ""}${seconds} sec`;
  };
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <>
      {
        <div css={messageRow}>
          <div>
            <div css={messageBlue}>
              <p css={messageContent}>
                <TableContainer
                  component={Box}
                  sx={{ overflow: "auto", backgroundColor: "white" }}
                >
                  {/* <div id="content-to-pdf" {...console.log(content)} dangerouslySetInnerHTML={{ __html: content }} /> */}
                  <div
                    id="content-to-pdf"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </TableContainer>
                {citations?.length != 0 && (
                  <>
                    {!isApiError && !isTimeoutError && (
                      <div className="font-bold ">Sources</div>
                    )}
                    <ul>
                      {citations?.map((citation, index) => (
                        <li key={index}>
                          <Button
                            variant="outlined"
                            startIcon={<Article />}
                            sx={{
                              textTransform: "none",
                              maxWidth: "30rem",
                              margin: "0.2rem 0 0.2rem 0",
                            }}
                          >
                            <Sheet>
                              <SheetTrigger>
                                {citation?.source?.split("/").at(-1)}
                              </SheetTrigger>
                              <SheetContent style={{ maxWidth: "50vw" }}>
                                <SheetHeader>
                                  <SheetTitle>
                                    {citation?.source?.split("/").at(-1)}
                                  </SheetTitle>
                                  <SheetDescription>
                                    <div className="font-semibold">
                                      {citation?.pages &&
                                        `page no. ${citation?.pages?.toString()}`}
                                    </div>
                                    <PDFViewer gcsPath={citation?.source} />
                                  </SheetDescription>
                                </SheetHeader>
                              </SheetContent>
                            </Sheet>
                          </Button>
                          {citation?.pages?.at(0)?.length !== 0 &&
                            `(p. ${citation?.pages?.toString()})`}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </p>
              {responseTime && (
                <div
                  style={{
                    color: "gray",
                    fontSize: "0.85rem",
                    marginTop: "0.5rem",
                    padding: "0 1rem",
                  }}
                >
                  Response Time: {formatResponseTime(responseTime)}
                </div>
              )}
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#BCEDAF",
                  display: "flex",
                  flexDirection: "row-reverse",
                  borderRadius: "10px",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handle_messageversion}
                  disabled={loadversion || user_edit || loading || covloader}
                  sx={{
                    margin: "0.5rem",
                    textTransform: "capitalize",
                    padding: "1px 10px",
                  }}
                >
                  Save Version
                </Button>
                {messageId !== "fallback" && <FeedbackPopup
                  open={feedbackOpen}
                  onOpenChange={setFeedbackOpen}
                  responseId={messageId}
                  isFeedbackButtonDisabled={
                    loadversion || user_edit || loading || covloader
                  }
                  variant="default"
                  cssClass={`h-0 mt-2 py-3 rounded-sm
                    ${loadversion || user_edit || loading || covloader ? "bg-[#0000001F] text-[#0009]" : "bg-[#137d00] hover:bg-[#0D5700]"}`}
                  gaFeedbackType={"generation"}
                />}
              </div>
            </div>
          </div>
        </div>
      }
    </>
  );
};

export const MessageRight = (props: any) => {
  const { message = "no message", timestamp = "", displayName, isLastUserMessage } = props;
  return (
    <div css={messageRowRight}>
      <div>
        {/* <div css={displayName1}>{displayName}</div> */}
        <div css={messageOrange}>
          <p css={messageContent}>{message}</p>
          {props?.stoppedByUser && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <CancelIcon color="error" />
                </TooltipTrigger>
                <TooltipContent className="bg-black">
                  <p className="text-white">This message was stopped</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isLastUserMessage && (
            <Button>Edit</Button>
          )}
        </div>
      </div>
    </div>
  );
};
